import type { 
  GameState, SeatPlayer, PlayerAction, 
  ShowdownResult, PositionTag, GameMode 
} from '@/types';
import { createDeck, shuffleDeck, dealCards } from './deck';
import { evaluateHand, compareHands } from './hand-evaluator';
import type { HandEvaluation } from '@/types';
import { PotManager } from './pot-manager';
import { GAME_CONFIGS, POSITION_ORDER, AI_NAMES, AVATARS } from '@/utils/constants';

export class TexasHoldemEngine {
  private state: GameState;
  private potManager: PotManager;
  private lastRaiseAmount: number = 0;
  private config: typeof GAME_CONFIGS[GameMode];

  constructor(mode: GameMode = 'casual', humanPlayer?: { id: string; name: string; avatar: string }) {
    this.config = GAME_CONFIGS[mode];
    const players = this.initializePlayers(mode, humanPlayer);
    
    this.state = {
      phase: 'waiting',
      deck: [],
      communityCards: [],
      players,
      dealerIndex: 0,
      currentPlayerIndex: 0,
      pots: [],
      currentBet: 0,
      minRaise: this.config.bigBlind,
      blinds: { small: this.config.smallBlind, big: this.config.bigBlind },
      handNumber: 0,
      mode,
      timestamp: Date.now(),
    };

    this.potManager = new PotManager(players);
  }

  private initializePlayers(mode: GameMode, humanPlayer?: { id: string; name: string; avatar: string }): SeatPlayer[] {
    const players: SeatPlayer[] = [];
    const numAI = Math.min(
      Math.max(2, Math.floor(Math.random() * 3) + 3), // 3-5 个AI
      this.config.maxPlayers - 1
    );

    // 添加真人玩家
    if (humanPlayer) {
      players.push({
        id: humanPlayer.id,
        name: humanPlayer.name,
        isHuman: true,
        chips: mode.startsWith('tournament') ? this.config.startingChips! : 10000,
        holeCards: null,
        currentBet: 0,
        totalBetThisRound: 0,
        status: 'active',
        position: 'BTN',
        avatar: humanPlayer.avatar,
      });
    }

    // 添加 AI 玩家
    const usedNames = new Set<string>();
    for (let i = 0; i < numAI; i++) {
      let name = AI_NAMES[Math.floor(Math.random() * AI_NAMES.length)];
      while (usedNames.has(name)) {
        name = AI_NAMES[Math.floor(Math.random() * AI_NAMES.length)];
      }
      usedNames.add(name);

      const aiLevels: ('easy' | 'medium' | 'hard')[] = ['easy', 'easy', 'medium', 'medium', 'hard'];
      const aiLevel = mode === 'casual' ? 'easy' : aiLevels[Math.floor(Math.random() * aiLevels.length)];

      players.push({
        id: `ai-${i}`,
        name,
        isHuman: false,
        aiLevel,
        chips: mode.startsWith('tournament') ? this.config.startingChips! : 10000 + Math.floor(Math.random() * 5000),
        holeCards: null,
        currentBet: 0,
        totalBetThisRound: 0,
        status: 'active',
        position: 'SB',
        avatar: AVATARS[Math.floor(Math.random() * AVATARS.length)],
      });
    }

    // 分配位置
    this.assignPositions(players);

    return players;
  }

  private assignPositions(players: SeatPlayer[]): void {
    const positions = POSITION_ORDER[players.length] || POSITION_ORDER[6];
    players.forEach((player, index) => {
      player.position = positions[index] as PositionTag;
    });
  }

  startNewHand(): GameState {
    this.state.handNumber++;
    this.state.phase = 'preflop';
    this.state.communityCards = [];
    this.state.currentBet = 0;
    this.state.minRaise = this.config.bigBlind;
    this.lastRaiseAmount = this.config.bigBlind;

    // 重置玩家状态
    this.state.players.forEach(player => {
      if (player.chips > 0 && player.status !== 'eliminated') {
        player.status = 'active';
        player.holeCards = null;
        player.currentBet = 0;
        player.totalBetThisRound = 0;
      }
    });

    // 移动庄家位置
    do {
      this.state.dealerIndex = (this.state.dealerIndex + 1) % this.state.players.length;
    } while (this.state.players[this.state.dealerIndex].status === 'eliminated');

    // 创建并洗牌
    this.state.deck = shuffleDeck(createDeck());

    // 发底牌
    this.dealHoleCards();

    // 收取盲注
    this.collectBlinds();

    // 重置底池
    this.potManager.resetPots(this.state.players);

    // 设置当前玩家（UTG）
    this.setNextActivePlayer(this.getUTGIndex());

    return this.getState();
  }

  private dealHoleCards(): void {
    const activePlayers = this.state.players.filter(p => p.status !== 'eliminated');
    
    for (let i = 0; i < 2; i++) {
      for (const player of activePlayers) {
        const { cards, remaining } = dealCards(this.state.deck, 1);
        if (player.holeCards === null) {
          player.holeCards = [cards[0], cards[0]]; // 临时占位
        }
        if (i === 0) {
          player.holeCards = [cards[0], player.holeCards[1]];
        } else {
          player.holeCards[1] = cards[0];
        }
        this.state.deck = remaining;
      }
    }
  }

  private collectBlinds(): void {
    const numPlayers = this.state.players.length;
    const sbIndex = (this.state.dealerIndex + 1) % numPlayers;
    const bbIndex = (this.state.dealerIndex + 2) % numPlayers;

    const sbPlayer = this.state.players[sbIndex];
    const bbPlayer = this.state.players[bbIndex];

    // 小盲
    const sbAmount = Math.min(this.config.smallBlind, sbPlayer.chips);
    sbPlayer.chips -= sbAmount;
    sbPlayer.currentBet = sbAmount;
    sbPlayer.totalBetThisRound = sbAmount;
    this.potManager.handleBet(sbPlayer, sbAmount);

    // 大盲
    const bbAmount = Math.min(this.config.bigBlind, bbPlayer.chips);
    bbPlayer.chips -= bbAmount;
    bbPlayer.currentBet = bbAmount;
    bbPlayer.totalBetThisRound = bbAmount;
    this.potManager.handleBet(bbPlayer, bbAmount);

    this.state.currentBet = bbAmount;
  }

  private getUTGIndex(): number {
    const numPlayers = this.state.players.length;
    // UTG 是大盲后的第一个玩家
    return (this.state.dealerIndex + 3) % numPlayers;
  }

  private setNextActivePlayer(startIndex: number): void {
    let index = startIndex;
    const numPlayers = this.state.players.length;

    for (let i = 0; i < numPlayers; i++) {
      const player = this.state.players[index];
      if (player.status === 'active' && player.chips > 0) {
        this.state.currentPlayerIndex = index;
        return;
      }
      index = (index + 1) % numPlayers;
    }

    // 没有找到活跃玩家，进入下一阶段
    this.advancePhase();
  }

  handleAction(playerId: string, action: PlayerAction): GameState {
    const player = this.state.players[this.state.currentPlayerIndex];
    
    if (player.id !== playerId || player.status !== 'active') {
      return this.getState();
    }

    switch (action.type) {
      case 'fold':
        player.status = 'folded';
        break;

      case 'check':
        // 只有在不需要跟注时才能过牌
        if (player.currentBet < this.state.currentBet) {
          return this.getState(); // 无效操作
        }
        break;

      case 'call':
        const callAmount = Math.min(
          this.state.currentBet - player.currentBet,
          player.chips
        );
        player.chips -= callAmount;
        player.currentBet += callAmount;
        player.totalBetThisRound += callAmount;
        this.potManager.handleBet(player, callAmount);
        
        if (player.chips === 0) {
          player.status = 'all-in';
          this.potManager.createSidePots(this.state.players);
        }
        break;

      case 'raise':
        if (!action.amount || action.amount < this.state.minRaise) {
          return this.getState(); // 无效加注
        }
        
        const raiseAmount = Math.min(action.amount, player.chips + player.currentBet);
        const actualRaise = raiseAmount - player.currentBet;
        
        player.chips -= actualRaise;
        player.currentBet = raiseAmount;
        player.totalBetThisRound += actualRaise;
        
        this.lastRaiseAmount = raiseAmount - this.state.currentBet;
        this.state.currentBet = raiseAmount;
        this.state.minRaise = raiseAmount + this.lastRaiseAmount;
        
        this.potManager.handleBet(player, actualRaise);
        
        if (player.chips === 0) {
          player.status = 'all-in';
          this.potManager.createSidePots(this.state.players);
        }
        break;

      case 'all-in':
        const allInAmount = player.chips + player.currentBet;
        const actualAllIn = player.chips;
        
        if (allInAmount > this.state.currentBet) {
          // 这是一个加注
          this.lastRaiseAmount = allInAmount - this.state.currentBet;
          this.state.minRaise = allInAmount + this.lastRaiseAmount;
        }
        
        player.chips = 0;
        player.currentBet = allInAmount;
        player.totalBetThisRound += actualAllIn;
        player.status = 'all-in';
        
        this.state.currentBet = Math.max(this.state.currentBet, allInAmount);
        this.potManager.handleBet(player, actualAllIn);
        this.potManager.createSidePots(this.state.players);
        break;
    }

    // 检查是否进入下一阶段
    if (this.isPhaseComplete()) {
      this.advancePhase();
    } else {
      this.setNextActivePlayer((this.state.currentPlayerIndex + 1) % this.state.players.length);
    }

    return this.getState();
  }

  private isPhaseComplete(): boolean {
    const activePlayers = this.state.players.filter(
      p => p.status === 'active' || p.status === 'all-in'
    );

    // 如果只剩一个活跃玩家，结束这手牌
    const nonFoldedPlayers = this.state.players.filter(p => p.status !== 'folded' && p.status !== 'eliminated');
    if (nonFoldedPlayers.length === 1) {
      return true;
    }

    // 检查是否所有活跃玩家都已下注相等
    const allBetsEqual = activePlayers.every(
      p => p.currentBet === this.state.currentBet || p.status === 'all-in'
    );

    return allBetsEqual;
  }

  private advancePhase(): void {
    // 重置本轮下注
    this.state.players.forEach(p => {
      p.currentBet = 0;
    });
    this.state.currentBet = 0;
    this.state.minRaise = this.config.bigBlind;
    this.lastRaiseAmount = this.config.bigBlind;

    switch (this.state.phase) {
      case 'preflop':
        this.state.phase = 'flop';
        this.dealCommunityCards(3);
        break;
      case 'flop':
        this.state.phase = 'turn';
        this.dealCommunityCards(1);
        break;
      case 'turn':
        this.state.phase = 'river';
        this.dealCommunityCards(1);
        break;
      case 'river':
        this.state.phase = 'showdown';
        this.handleShowdown();
        return;
    }

    // 找到第一个活跃玩家（小盲后）
    const sbIndex = (this.state.dealerIndex + 1) % this.state.players.length;
    this.setNextActivePlayer(sbIndex);
  }

  private dealCommunityCards(count: number): void {
    const { cards, remaining } = dealCards(this.state.deck, count);
    this.state.communityCards.push(...cards);
    this.state.deck = remaining;
  }

  private handleShowdown(): void {
    this.state.phase = 'settlement';
    
    // 如果只剩一个未弃牌玩家，直接获胜
    const nonFoldedPlayers = this.state.players.filter(
      p => p.status !== 'folded' && p.status !== 'eliminated'
    );
    
    if (nonFoldedPlayers.length === 1) {
      // 该玩家赢得所有底池
      const winner = nonFoldedPlayers[0];
      const totalPot = this.potManager.getTotalPotAmount();
      winner.chips += totalPot;
      return;
    }

    // 摊牌比大小
    const results = this.evaluateShowdown();
    this.settlePots(results);
  }

  private evaluateShowdown(): ShowdownResult {
    const evaluations: { playerId: string; evaluation: HandEvaluation }[] = [];

    for (const player of this.state.players) {
      if (player.status !== 'folded' && player.status !== 'eliminated' && player.holeCards) {
        const eval_ = evaluateHand(player.holeCards, this.state.communityCards);
        evaluations.push({ playerId: player.id, evaluation: eval_ });
      }
    }

    // 按底池分别比较
    const pots = this.potManager.getPots();
    const winners: { playerId: string; potId: number; amount: number }[] = [];

    for (const pot of pots) {
      const potEvaluations = evaluations.filter(e => pot.eligiblePlayers.includes(e.playerId));
      if (potEvaluations.length === 0) continue;

      // 找出最大牌型
      let bestEval = potEvaluations[0];
      const potWinners = [bestEval];

      for (let i = 1; i < potEvaluations.length; i++) {
        const comparison = compareHands(potEvaluations[i].evaluation, bestEval.evaluation);
        if (comparison > 0) {
          potWinners.length = 0;
          bestEval = potEvaluations[i];
          potWinners.push(bestEval);
        } else if (comparison === 0) {
          potWinners.push(potEvaluations[i]);
        }
      }

      // 平分底池
      const share = Math.floor(pot.amount / potWinners.length);
      const remainder = pot.amount % potWinners.length;

      potWinners.forEach((w, index) => {
        winners.push({
          playerId: w.playerId,
          potId: pot.id,
          amount: share + (index === 0 ? remainder : 0),
        });
      });
    }

    return { winners, handEvaluations: evaluations };
  }

  private settlePots(result: ShowdownResult): void {
    // 汇总每个玩家的赢取金额
    const winnings = new Map<string, number>();
    
    for (const win of result.winners) {
      const current = winnings.get(win.playerId) || 0;
      winnings.set(win.playerId, current + win.amount);
    }

    // 分配筹码
    for (const [playerId, amount] of winnings) {
      const player = this.state.players.find(p => p.id === playerId);
      if (player) {
        player.chips += amount;
      }
    }
  }

  getState(): GameState {
    return {
      ...this.state,
      pots: this.potManager.getPots(),
    };
  }

  getPotManager(): PotManager {
    return this.potManager;
  }

  getValidActions(playerId: string): string[] {
    const player = this.state.players.find(p => p.id === playerId);
    if (!player || player.status !== 'active') return [];

    const actions: string[] = ['fold'];

    const toCall = this.state.currentBet - player.currentBet;

    if (toCall === 0) {
      actions.push('check');
    }

    if (player.chips > toCall) {
      actions.push('call');
      actions.push('raise');
    }

    if (player.chips > 0) {
      actions.push('all-in');
    }

    return actions;
  }

  getCurrentPlayer(): SeatPlayer | null {
    return this.state.players[this.state.currentPlayerIndex] || null;
  }

  isGameOver(): boolean {
    // 锦标赛：只剩一个玩家有筹码
    if (this.state.mode !== 'casual') {
      const playersWithChips = this.state.players.filter(p => p.chips > 0);
      return playersWithChips.length <= 1;
    }
    return false;
  }

  getWinners(): SeatPlayer[] {
    return this.state.players.filter(p => p.chips > 0);
  }
}
